import { useMemo } from 'react';
import { Empty } from 'antd';
import type { CallbackDataParams } from 'echarts/types/dist/shared';

import type { BarChartOption } from '../../../../models/echarts';
import { ReactECharts } from '../../../charts/ReactECharts';
import type { BarLineData } from '../programDashboardSlice';

const getOption = (data: [BarLineData, BarLineData] | null, rotate: boolean) => {
  if (!data || data[0].data.length === 0 || data[0].data[0] === null) {
    return;
  }

  // @ts-expect-error
  const series: BarChartOption['series'] = [data[0]];
  const xAxisData = data[1].data;

  const option: BarChartOption = {
    grid: {
      bottom: 0,
      left: 0,
      top: 10,
      right: 0,
    },
    tooltip: {
      trigger: 'axis',
      padding: [10, 10],
      confine: true,
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params) => {
        const p = params as CallbackDataParams[];
        const { name, value, seriesName } = p[0];
        return `${name}: ${value}${seriesName === 'Percentage' ? '%' : ''}`;
      },
    },
    legend: {
      show: false,
    },
    color: ['#1890FF'],
    xAxis: {
      show: true,
      type: 'category',
      splitLine: {
        show: false,
      },
      axisTick: {
        alignWithLabel: true,
      },
      axisLabel: {
        show: true,
        fontWeight: 'bold',
      },
      data: xAxisData,
    },
    yAxis: {
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        show: false,
      },
      splitLine: {
        show: true,
        interval: 0,
        lineStyle: {
          type: 'solid',
          opacity: 0.24,
        },
      },
    },
    series,
  };

  if (rotate) {
    const y = option.yAxis;
    const x = option.xAxis;
    option.xAxis = y as BarChartOption['xAxis'];
    option.yAxis = {
      ...x,
      axisLine: {
        show: false,
      },
    } as BarChartOption['yAxis'];
  }

  return option;
};

export const DashboardBarChartComponent = ({
  data,
  rotate,
}: {
  data: [BarLineData, BarLineData] | null;
  rotate: boolean;
}) => {
  const option = useMemo(() => getOption(data, rotate), [data, rotate]);
  const length = data?.[0].data.length ?? 0;
  const legendHeight = length > 5 ? 250 + length * 4 : 220;

  return option ? (
    <ReactECharts option={option} style={{ padding: 0, height: legendHeight, marginTop: 0, minHeight: legendHeight }} />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 20 }} />
  );
};
